import { useEffect, useState } from 'react';

/**
 * Music / Song Requests — v2 port of v1's music.ejs.
 * Shows currently playing song + queue. Backend wiring TBD; in the meantime
 * accepts queue via config or polls a configured API URL.
 *
 * Config (all optional):
 *   apiUrl: string                 polled every `pollSeconds` for live state
 *   pollSeconds: number            (default 8)
 *   queue: SongDef[]               static fallback / preview
 *   accentColor: string            (default '#a78bfa')
 *   showQueue: boolean             (default true)
 *   maxQueueRows: number           (default 5)
 *   title: string                  (default 'Now Playing')
 *
 * SongDef: { id, title, requester?, thumbnail?, duration? (seconds) }
 *
 * API response contract: { current: SongDef | null, queue: SongDef[] }
 */

interface SongDef {
  id: string;
  title: string;
  requester?: string;
  thumbnail?: string;
  duration?: number;
}

interface MusicState {
  current: SongDef | null;
  queue: SongDef[];
}

function fmtDuration(sec?: number): string {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export function MusicWidget({ fields }: { fields: Record<string, unknown> }) {
  const apiUrl = (fields.apiUrl as string | undefined) ?? null;
  const pollSeconds = Number(fields.pollSeconds ?? 8);
  const accent = String(fields.accentColor ?? '#a78bfa');
  const title = String(fields.title ?? 'Now Playing');
  const showQueue = (fields.showQueue as boolean | undefined) ?? true;
  const maxQueueRows = Number(fields.maxQueueRows ?? 5);
  const staticQueue = (fields.queue as SongDef[] | undefined) ?? [];

  const [state, setState] = useState<MusicState>({
    current: staticQueue[0] ?? null,
    queue: staticQueue.slice(1),
  });

  useEffect(() => {
    if (!apiUrl) return;
    let cancelled = false;
    const fetchState = async () => {
      try {
        const res = await fetch(apiUrl);
        if (!res.ok) return;
        const data = (await res.json()) as MusicState;
        if (!cancelled) setState(data);
      } catch {
        /* swallow */
      }
    };
    void fetchState();
    const t = setInterval(fetchState, pollSeconds * 1000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [apiUrl, pollSeconds]);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: '14px 18px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 320,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ color: accent }}>♪</span>
        {title}
        {state.queue.length > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontWeight: 700,
              color: accent,
              background: `${accent}1f`,
              borderRadius: 4,
              padding: '1px 6px',
            }}
          >
            +{state.queue.length} in queue
          </span>
        )}
      </div>

      {state.current ? (
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: showQueue && state.queue.length ? 14 : 0,
          }}
        >
          {state.current.thumbnail ? (
            <img
              alt=""
              src={state.current.thumbnail}
              style={{
                width: 56,
                height: 56,
                borderRadius: 8,
                objectFit: 'cover',
                flexShrink: 0,
                boxShadow: `0 0 16px ${accent}40`,
              }}
            />
          ) : (
            <div
              style={{
                width: 56,
                height: 56,
                borderRadius: 8,
                background: `linear-gradient(135deg, ${accent}, ${accent}88)`,
                display: 'grid',
                placeItems: 'center',
                fontSize: 26,
                flexShrink: 0,
              }}
            >
              ♪
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontWeight: 800,
                fontSize: 15,
                lineHeight: 1.25,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {state.current.title}
            </div>
            <div
              style={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.6)',
                marginTop: 3,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              {state.current.requester && <span>by {state.current.requester}</span>}
              {state.current.duration && (
                <span style={{ color: accent, fontVariantNumeric: 'tabular-nums' }}>
                  {fmtDuration(state.current.duration)}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>Nothing playing</div>
      )}

      {showQueue && state.queue.length > 0 && (
        <div>
          <div
            style={{
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: 1.2,
              textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.4)',
              marginBottom: 6,
            }}
          >
            Up next
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {state.queue.slice(0, maxQueueRows).map((s, i) => (
              <div
                key={s.id}
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 8,
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.7)',
                }}
              >
                <span style={{ color: accent, fontWeight: 700, width: 16 }}>{i + 1}</span>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.title}
                </span>
                {s.requester && <span style={{ fontSize: 10, opacity: 0.55 }}>{s.requester}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
